import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  Door,
  Location,
  Mutation,
  MutationAddLocationArgs,
  MutationAssignToAccessGrantsArgs,
  MutationUpdateLocationArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import { ADD_DEVICE_GROUP, ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS, UPDATE_DEVICE_GROUP } from 'graphql/v2/mutations';
import {
  GET_ALL_DOORS_FOR_DEVICE_GROUPS,
  GET_ALL_VISITORS_GROUPS,
  GET_DEVICE_GROUPS_WITH_DOORS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';

export type DeviceGroupRow = Location & { userGroups: VisitorGroup[] };

const useDeviceGroups = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();
  const {
    loading: deviceGroupsLoading,
    error: deviceGroupsError,
    data: deviceGroupsData,
    networkStatus: deviceGroupsNetworkStatus
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_DEVICE_GROUPS_WITH_DOORS, {
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData,
    networkStatus: visitorGroupsNetworkStatus
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_GROUPS, {
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData,
    networkStatus: doorsNetworkStatus
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_ALL_DOORS_FOR_DEVICE_GROUPS, {
    notifyOnNetworkStatusChange: true
  });

  const loading =
    (deviceGroupsLoading && !deviceGroupsData) ||
    (visitorGroupsLoading && !visitorGroupsData) ||
    (doorsLoading && !doorsData);

  const isRefetchingDeviceGroups =
    !loading &&
    ((!!deviceGroupsData && deviceGroupsNetworkStatus !== NetworkStatus.ready) ||
      (!!visitorGroupsData && visitorGroupsNetworkStatus !== NetworkStatus.ready) ||
      (!!doorsData && doorsNetworkStatus !== NetworkStatus.ready));

  useRegisterBackgroundRefetch(isRefetchingDeviceGroups);

  useEffect(() => {
    if (deviceGroupsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(deviceGroupsError);
    }
  }, [deviceGroupsError]);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.device_group_error_fetching_user_groups);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const allLocations = deviceGroupsData?.getLocationsForTenant ?? [];
  const allVisitorGroups = visitorGroupsData?.getVisitorGroupsForTenant ?? [];
  const allDoors: Door[] = doorsData?.getDoorsForTenant ?? [];

  const deviceGroupsWithUserGroups: DeviceGroupRow[] = allLocations.map((location) => {
    const userGroups = allVisitorGroups.filter((group) =>
      group.accessGrants.some((grant) => grant.locations.some((loc) => loc.id === location.id))
    );
    return { ...location, userGroups };
  });

  const [addDeviceGroupMutation, { loading: addDeviceGroupLoading, error: addDeviceGroupError }] = useMutation<
    Pick<Mutation, 'addLocation'>,
    MutationAddLocationArgs
  >(ADD_DEVICE_GROUP, {
    refetchQueries: [GET_DEVICE_GROUPS_WITH_DOORS, GET_ALL_VISITORS_GROUPS],
    awaitRefetchQueries: true
  });

  const [assignToAccessGrantsMutation, { error: assignError }] = useMutation<
    Pick<Mutation, 'assignToAccessGrants'>,
    MutationAssignToAccessGrantsArgs
  >(ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS, {
    refetchQueries: [GET_ALL_VISITORS_GROUPS],
    awaitRefetchQueries: true
  });

  const [updateDeviceGroupMutation, { error: updateDeviceGroupError }] = useMutation<
    Pick<Mutation, 'updateLocation'>,
    MutationUpdateLocationArgs
  >(UPDATE_DEVICE_GROUP, {
    refetchQueries: (result) => [
      { query: GET_DEVICE_GROUPS_WITH_DOORS, variables: { filter: { id: result.data?.updateLocation?.id } } }
    ],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (addDeviceGroupError) {
      handleFetchError(addDeviceGroupError.message);
      console.error(addDeviceGroupError);
    }
  }, [addDeviceGroupError]);

  useEffect(() => {
    if (assignError) {
      handleFetchError(assignError.message);
      console.error(assignError);
    }
  }, [assignError]);

  useEffect(() => {
    if (updateDeviceGroupError) {
      handleFetchError(updateDeviceGroupError.message);
      console.error(updateDeviceGroupError);
    }
  }, [updateDeviceGroupError]);

  const createDeviceGroup = async (
    name: string
  ): Promise<{ success: boolean; groupId?: string; errorMessage?: string }> => {
    try {
      const result = await addDeviceGroupMutation({
        variables: { location: { name, doorIds: [] } }
      });
      const groupId = result.data?.addLocation?.id;
      if (!groupId) return { success: false, errorMessage: 'Failed to create device group' };
      return { success: true, groupId };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const assignUserGroupsToDeviceGroup = async (
    groupId: string,
    visitorGroupIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      const selectedGroups = allVisitorGroups.filter((group) => visitorGroupIds.includes(group.id));
      const accessGrantIds = selectedGroups.flatMap((group) =>
        getAllAccessGrantsOfVisitorGroup(group).map((grant) => grant.id)
      );
      if (accessGrantIds.length > 0) {
        await assignToAccessGrantsMutation({
          variables: { accessGrantIds, doorIds: [], locationIds: [groupId] }
        });
      }
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const updateDeviceGroupWithDoors = async (
    groupId: string,
    groupName: string,
    doorIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      await updateDeviceGroupMutation({
        variables: { id: groupId, location: { name: groupName, doorIds } }
      });
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  return {
    deviceGroupsWithUserGroups,
    allDoors,
    allVisitorGroups,
    loading,
    createDeviceGroup,
    assignUserGroupsToDeviceGroup,
    updateDeviceGroupWithDoors,
    addDeviceGroupLoading,
    isRefetchingDeviceGroups
  };
};

export default useDeviceGroups;
